import { Do, ReportVersion, fsmData, } from '../../../../universal';
import { React, _ } from '../../../lib';
import { CustomPDFContainer } from '../../component-pdf-viewer';
import { LoadingSpinnerProgress } from '../../component-progress';
import { Button } from '../buttons';
import { CJSX, ConditionalObject, useRSInstance } from '../meta-types';
import { ResizeSplitter } from '../resize-split';
import { SearchBox } from '../textbox';
import { Toolbar } from '../toolbar';
import { TreeList } from '../tree-list';
import { Action, getPartialStateFromAction, sendOnUpdate } from './actions';
import { ReportOptionPane, getComponentType } from './options';
const getDefaultState = (props) => ({
    widths: props.view?.widths,
    selectedReport: props.view?.selectedReport,
    expanded: props.view?.expanded ?? [],
    options: props.view?.options ?? {},
    defaultOptions: props.defaultOptions ?? {},
    searchText: props.view?.searchText ?? '',
    pdfURL: props.view?.pdfs ?? {},
    cache: null,
    isLoading: {},
    loadProg: {},
    errorMessage: {},
    iframeSrc: {},
});
export const ReportFrameComponent = (props, ref) => {
    const pdfContainer = React.useRef(null);
    const treeList = React.useRef(null);
    const rs = useRSInstance({
        props: props,
        refs: { pdfContainer, treeList },
        defaultState: getDefaultState,
        actionToDelta: getPartialStateFromAction,
    });
    React.useEffect(() => {
        rs.dispatch([Action.UpdateState, () => props.view ?? {}]);
    }, [
        props.view?.selectedReport,
        props.view?.widths,
        props.view?.expanded,
        props.view?.searchText,
    ]);
    React.useEffect(() => {
        setTimeout(() => {
            rs.refs.treeList.current?.getList().refreshScrollJack();
        }, 10);
    }, []);
    React.useEffect(() => {
        loadParameters(rs).catch(err => {
            console.error(err);
        });
    }, [props.loadData.v3, props.loadData.v4, rs]);
    React.useImperativeHandle(ref, () => ({
        rs: rs,
        pdfContainer: pdfContainer,
    }));
    const rpt = rs.state.selectedReport ?? '';
    const isLoading = rs.state.isLoading[rpt];
    const loadProg = rs.state.loadProg[rpt];
    const errorMessage = rs.state.errorMessage[rpt];
    const pdfURL = rs.state.pdfURL[rpt];
    const iframeSrc = rs.state.iframeSrc[rpt];
    return (React.createElement("div", { className: "tailwind-wrapper", style: { width: '100%', height: '100%' } },
        React.createElement(ResizeSplitter, { className: "flex w-full h-full", value: rs.state.widths, onUpdate: v => {
                rs.dispatch([Action.UpdateState, () => ({ widths: v })]);
                sendOnUpdate(rs, { widths: v });
            }, panes: [
                {
                    defaultWidth: 200,
                    minWidth: 160,
                    maxWidth: 400,
                    flexWidth: null,
                    collapsible: true,
                    content: (React.createElement(React.Fragment, null,
                        React.createElement(ReportToolbar, { rs: rs }),
                        React.createElement(ReportList, { rs: rs }))),
                },
                {
                    defaultWidth: 290,
                    minWidth: 200,
                    maxWidth: 400,
                    flexWidth: null,
                    collapsible: true,
                    content: (React.createElement("div", { className: "w-full h-full overflow-y-auto border-l border-l-[#ccc]" },
                        React.createElement(ReportOptionPane, { rs: rs, report: rs.state.selectedReport ?? null }))),
                },
                {
                    defaultWidth: null,
                    minWidth: null,
                    maxWidth: null,
                    flexWidth: 1,
                    collapsible: true,
                    content: (React.createElement("div", { className: "w-full h-full bg-[#ddd] border-l border-l-[#ccc] overflow-y-hidden" }, Do(() => {
                        if (isLoading) {
                            return (React.createElement(LoadingSpinnerProgress, { type: "full", report: loadProg ?? undefined }));
                        }
                        else if (errorMessage) {
                            return (React.createElement("div", { className: "inline-block absolute top-1/3 left-1/2 text-[#e00] font-light text-xl -translate-x-1/2 -translate-y-1/2" }, errorMessage));
                        }
                        else if (pdfURL != null) {
                            return (React.createElement(CustomPDFContainer, { ref: pdfContainer, url: pdfURL }));
                        }
                        else if (iframeSrc != null) {
                            return (React.createElement("div", { className: "w-full h-full" },
                                React.createElement("iframe", { src: iframeSrc, className: "w-full h-full overflow-hidden border-none absolute pointer-events-none opacity-[0.01]" }),
                                React.createElement("div", { className: "absolute w-full top-1/3 -translate-y-1/2 text-center text-xl font-light text-[#444]" }, "Complete. Your browser should download the report automatically.")));
                        }
                        return (React.createElement("div", { className: "block font-light font-condensed text-xl mt-20 text-center text-[#888]" }, "Build a report"));
                    }))),
                },
            ] })));
};
const ReportToolbar = (props) => (React.createElement(Toolbar, { widthRHS: 90, lhs: React.createElement(SearchBox, { className: "w-full", value: props.rs.state.searchText, onUpdate: v => {
            props.rs.dispatch([Action.UpdateState, () => ({ searchText: v })]);
            sendOnUpdate(props.rs, { searchText: v });
        }, onArrowDelta: (delta, shiftKey) => {
            const list = props.rs.refs.treeList.current?.getList();
            list?.moveSelection(delta, shiftKey);
        } }), rhs: React.createElement(CJSX, { cond: props.rs.props.reportJobs?.openWindow != null },
        React.createElement(Button, { type: "ui5-borderless-24", img: "/static/img/i8/office-report.svg", lbl: "Jobs", title: "Open the report job manager", onClick: () => {
                props.rs.props.reportJobs?.openWindow?.(null);
            } })) }));
const ReportList = (props) => (React.createElement(TreeList, { className: "w-full bg-[#eee]", style: { height: 'calc(100% - 40px)' }, value: props.rs.state.selectedReport ?? null, ref: props.rs.refs.treeList, selectableGroups: true, onUpdate: v => {
        props.rs.dispatch([
            Action.UpdateState,
            () => ({
                selectedReport: v,
            }),
        ]);
        sendOnUpdate(props.rs, { selectedReport: String(v) });
    }, searchText: props.rs.state.searchText, expanded: props.rs.state.expanded, onUpdateExpanded: v => {
        props.rs.dispatch([
            Action.UpdateState,
            () => ({
                expanded: v.map(String),
            }),
        ]);
        sendOnUpdate(props.rs, { expanded: v.map(String) });
    }, items: _.compact([
        ConditionalObject(props.rs.props.reportJobs != null &&
            _.size(props.rs.props.reportJobs?.items) > 0, () => ({
            value: 'report-jobs',
            label: 'Report Jobs',
            items: ConditionalObject(props.rs.props.reportJobs != null &&
                _.size(props.rs.props.reportJobs?.items) > 0, fsmData(props.rs.props.reportJobs?.items, {
                sort: x => x.Name,
                map: x => ({
                    value: String(-x.ID),
                    label: x.Name,
                }),
            })),
        })),
        ..._.map(props.rs.props.reportGroups, grp => grp.Items.length > 1
            ? {
                value: grp.ID,
                label: grp.Name,
                items: fsmData(grp.Items, {
                    filter: R => !R.Unavailable,
                    sort: R => R.ID,
                    map: R => ({
                        value: String(R.ID),
                        label: R.Title,
                    }),
                }),
            }
            : {
                value: String(grp.Items[0]?.ID),
                label: grp.Items[0]?.Title ?? 'Unknown group',
            }),
    ]) }));
export const loadParametersGeneric = async (loadData) => {
    const emptyCache = {
        params: {},
        exports: {},
    };
    const emptyCacheFn = async () => Promise.resolve(emptyCache);
    const optionsByVersion = [
        [ReportVersion.V3, loadData.v3 ?? emptyCacheFn],
        [ReportVersion.V4, loadData.v4 ?? emptyCacheFn],
    ];
    const caches = await Promise.all(optionsByVersion.map(async ([version, fn]) => {
        const cache = await fn();
        return [version, cache];
    }));
    const cachesVersioned = caches.map(cachePair => {
        const [version, cache] = cachePair;
        return cache
            ? {
                ...cache,
                version: _.fromPairs(_.keys(cache.params).map(k => [k, version])),
            }
            : { version: {}, ...emptyCache };
    });
    const cacheCombined = _.merge({ version: {} }, emptyCache, ...cachesVersioned);
    return cacheCombined;
};
const loadParameters = async (rs) => {
    const cacheCombined = await loadParametersGeneric(rs.props.loadData);
    const options = {};
    const defaultOptions = {};
    _.forEach(rs.props.reportIndex, x => {
        _.map(cacheCombined.params[x.Key] ?? [], param => {
            const preset_option = rs.state.options?.[x.Key]?.[param.Key];
            const vDefault = param.Default ?? getComponentType(rs, param.Type).defVal?.() ?? null;
            const v = preset_option ?? vDefault;
            if (options[x.Key] == null) {
                options[x.Key] = {};
            }
            if (defaultOptions[x.Key] == null) {
                defaultOptions[x.Key] = {};
            }
            const optRecord = options[x.Key];
            if (optRecord) {
                optRecord[param.Key] = v;
            }
            const optDefRecord = defaultOptions[x.Key];
            if (optDefRecord) {
                optDefRecord[param.Key] = vDefault;
            }
        });
    });
    rs.dispatch([
        Action.UpdateState,
        () => ({
            cache: cacheCombined,
            options: options,
            defaultOptions: defaultOptions,
        }),
    ]);
};
//# sourceMappingURL=frame.js.map